import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { data, useLoaderData } from 'react-router'
import { EditExhibitionDrawer } from '#app/components/edit-exhibition-drawer.tsx'
import { ExhibitionDrawer } from '#app/components/exhibition-drawer.tsx'
import { NewsletterSignup } from '#app/components/newsletter-signup.tsx'
import { SendEventDetailsDialog } from '#app/components/send-event-details-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { type Language } from '#app/i18n.server.ts'
import { fadeInUp } from '#app/utils/animations.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { formatEventDateRange } from '#app/utils/event-dates.ts'
import { getNoteImgSrc } from '#app/utils/misc.tsx'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/exposities'

export const handle = { i18n: 'common' }

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const { getLocale } = await import('#app/i18n.server.ts')
	const language = (await getLocale(request)) as Language

	// Exhibition mutations (and the paid search endpoint) are admin-only, so
	// only admins get the create/edit controls.
	const isAdmin = userId
		? Boolean(
				await prisma.user.findFirst({
					where: { id: userId, roles: { some: { name: 'admin' } } },
					select: { id: true },
				}),
			)
		: false

	// Get current exhibitions/galleries/museums with image data
	const currentExhibitions = await prisma.exhibition.findMany({
		where: {
			OR: [
				{ type: 'exhibition', isCurrent: true },
				{ type: 'gallery' }, // Galleries are always current
				{ type: 'museum' }, // Museums are always current
				{ type: 'public_commission' }, // Public commissions are always current
			],
		},
		orderBy: [
			{ type: 'asc' }, // Galleries and museums first
			{ beginDate: 'desc' },
		],
		include: {
			createdBy: {
				select: {
					name: true,
					username: true,
				},
			},
		},
	})

	// Get past exhibitions
	const pastExhibitions = await prisma.exhibition.findMany({
		where: {
			type: 'exhibition',
			isCurrent: false,
		},
		orderBy: {
			endDate: 'desc',
		},
		include: {
			createdBy: {
				select: {
					name: true,
					username: true,
				},
			},
		},
	})

	// Get image data for exhibitions that have imageId
	const imageIds = [...currentExhibitions, ...pastExhibitions]
		.filter((e) => e.imageId)
		.map((e) => e.imageId as string)

	const images =
		imageIds.length > 0
			? await prisma.noteImage.findMany({
					where: { id: { in: imageIds } },
					select: { id: true, objectKey: true, altText: true },
				})
			: []

	// Get random beelden images as fallbacks
	const randomBeelden = await prisma.noteImage.findMany({
		where: {
			note: {
				owner: {
					username: 'ringnalda',
				},
			},
		},
		select: { id: true, objectKey: true, altText: true },
		take: 10,
		orderBy: {
			createdAt: 'desc',
		},
	})

	// Map images to exhibitions
	const imageMap = new Map(images.map((img) => [img.id, img]))

	// Separate galleries, museums, public commissions, and exhibitions
	const galleries = currentExhibitions.filter((e) => e.type === 'gallery')
	const museums = currentExhibitions.filter((e) => e.type === 'museum')
	const publicCommissions = currentExhibitions.filter(
		(e) => e.type === 'public_commission',
	)
	const currentExhibitionsOnly = currentExhibitions.filter(
		(e) => e.type === 'exhibition',
	)

	// Helper to get image for exhibition: own uploaded image first, then the
	// legacy NoteImage picker, then a random beeld as fallback
	const getExhibitionImage = (
		exhibition: (typeof currentExhibitions)[0],
		index: number,
	) => {
		if (exhibition.imageObjectKey) {
			return {
				id: exhibition.id,
				objectKey: exhibition.imageObjectKey,
				altText: exhibition.imageAltText,
			}
		}
		if (exhibition.imageId && imageMap.has(exhibition.imageId)) {
			return imageMap.get(exhibition.imageId)
		}
		return randomBeelden[index % randomBeelden.length]
	}

	// One batched translation lookup for all exhibitions (was an N+1)
	const allExhibitionIds = [...currentExhibitions, ...pastExhibitions].map(
		(e) => e.id,
	)
	const translationRows =
		allExhibitionIds.length > 0
			? await prisma.exhibitionTranslation.findMany({
					where: { exhibitionId: { in: allExhibitionIds }, language },
				})
			: []
	const translationMap = new Map(
		translationRows.map((t) => [t.exhibitionId, t]),
	)

	const addTranslation = (exhibition: (typeof currentExhibitions)[0]) => {
		const translation = translationMap.get(exhibition.id)
		return {
			...exhibition,
			title: translation?.title || exhibition.title,
			description: translation?.description || exhibition.description,
		}
	}

	const galleriesWithTranslations = galleries.map(addTranslation)
	const museumsWithTranslations = museums.map(addTranslation)
	const publicCommissionsWithTranslations = publicCommissions.map(addTranslation)
	const currentExhibitionsWithTranslations = currentExhibitionsOnly.map(addTranslation)
	const pastExhibitionsWithTranslations = pastExhibitions.map(addTranslation)

	return data({
		userId,
		isAdmin,
		galleries: galleriesWithTranslations.map((e, i) => ({
			...e,
			beginDate: e.beginDate?.toISOString() || null,
			endDate: e.endDate?.toISOString() || null,
			image: getExhibitionImage(e, i),
		})),
		museums: museumsWithTranslations.map((e, i) => ({
			...e,
			beginDate: e.beginDate?.toISOString() || null,
			endDate: e.endDate?.toISOString() || null,
			image: getExhibitionImage(e, i + galleries.length),
		})),
		publicCommissions: publicCommissionsWithTranslations.map((e, i) => ({
			...e,
			beginDate: e.beginDate?.toISOString() || null,
			endDate: e.endDate?.toISOString() || null,
			image: getExhibitionImage(e, i + galleries.length + museums.length),
		})),
		currentExhibitions: currentExhibitionsWithTranslations.map((e, i) => ({
			...e,
			beginDate: e.beginDate?.toISOString() || null,
			endDate: e.endDate?.toISOString() || null,
			image: getExhibitionImage(
				e,
				i + galleries.length + museums.length + publicCommissions.length,
			),
		})),
		pastExhibitions: pastExhibitionsWithTranslations.map((e, i) => ({
			...e,
			beginDate: e.beginDate?.toISOString() || null,
			endDate: e.endDate?.toISOString() || null,
			image: getExhibitionImage(
				e,
				i +
					galleries.length +
					museums.length +
					publicCommissions.length +
					currentExhibitionsOnly.length,
			),
		})),
	})
}

export default function ExhibitionsPage() {
	const {
		userId,
		isAdmin,
		galleries,
		museums,
		publicCommissions,
		currentExhibitions,
		pastExhibitions,
	} = useLoaderData<typeof loader>()
	const { t, i18n } = useTranslation()
	const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false)
	const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
	const [editingExhibition, setEditingExhibition] = useState<
		(typeof currentExhibitions)[0] | null
	>(null)
	const [sendDetailsExhibition, setSendDetailsExhibition] = useState<
		(typeof currentExhibitions)[0] | null
	>(null)

	// UTC-pinned shared helper: dates are stored at UTC midnight, so local-tz
	// formatting would cause hydration mismatches and off-by-one dates.
	const formatDateRange = (beginDate: string | null, endDate: string | null) =>
		formatEventDateRange(beginDate, endDate, i18n.language, {
			from: t('from'),
			until: t('until'),
		})

	const ExhibitionCard = ({
		exhibition,
	}: {
		exhibition: (typeof currentExhibitions)[0]
	}) => (
		<article className="group grid items-start gap-8" style={{ gridTemplateColumns: '320px 1fr' }}>
			{exhibition.image ? (
				<div className="overflow-hidden" style={{ background: 'var(--paper-2)' }}>
					<Img
						src={getNoteImgSrc(exhibition.image.objectKey)}
						alt={exhibition.image.altText || exhibition.title}
						width={640}
						height={800}
						fit="cover"
						className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
						style={{ aspectRatio: '4/5' }}
						loading="lazy"
					/>
				</div>
			) : (
				<div className="flex items-center justify-center" style={{ background: 'var(--paper-2)', aspectRatio: '4/5' }}>
					<Icon name="camera" className="h-8 w-8" style={{ color: 'var(--muted-2)' }} />
				</div>
			)}
			<div>
				{exhibition.beginDate && (
					<div className="eyebrow mb-2">
						{formatDateRange(exhibition.beginDate, exhibition.endDate)}
					</div>
				)}
				<h3 className="display mb-2" style={{ fontSize: 'clamp(24px, 3vw, 32px)' }}>
					{exhibition.title}
				</h3>
				<div className="text-[15px]" style={{ color: 'var(--ink-soft)' }}>
					{exhibition.address}
				</div>
				<div className="eyebrow mt-1">
					{exhibition.address?.split(',').pop()?.trim() || ''}
				</div>

				{exhibition.description && (
					<p className="mt-5 text-sm leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
						{exhibition.description}
					</p>
				)}

				<div className="mt-5 flex gap-4">
					{exhibition.websiteUrl && (
						<a href={exhibition.websiteUrl} target="_blank" rel="noopener noreferrer" className="btn-gallery-link text-sm">
							{t('website')} ↗
						</a>
					)}
					{exhibition.googleMapsUrl && (
						<a href={exhibition.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="btn-gallery-link text-sm">
							{t('location')} ↗
						</a>
					)}
					<button className="btn-gallery-link text-sm" onClick={() => setSendDetailsExhibition(exhibition)}>
						{t('keepMeInformed')}
					</button>
				</div>

				{isAdmin && (
					<button
						onClick={() => setEditingExhibition(exhibition)}
						className="mt-3 opacity-0 transition-opacity group-hover:opacity-100"
						style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-gallery)' }}
					>
						<Icon name="pencil-1" className="h-4 w-4" />
					</button>
				)}
			</div>
		</article>
	)

	const sections = [
		{ key: 'current', label: t('currentExhibitions') || 'Nu te zien', items: currentExhibitions },
		{ key: 'galleries', label: t('galleries') || 'Galeries', items: galleries },
		{ key: 'museums', label: t('museums') || 'Musea', items: museums },
		{ key: 'public', label: t('publicCommissions') || 'Opdrachten openbare ruimte', items: publicCommissions },
		{ key: 'past', label: t('pastExhibitions') || 'Selectie eerder werk', items: pastExhibitions },
	]

	return (
		<div>
			{/* Header */}
			<motion.section
				className="pt-[120px] pb-16"
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.15 }}
				variants={fadeInUp}
			>
				<div className="container">
					<div className="eyebrow mb-4">{t('expositionsAndPlacements')}</div>
					<h1 className="display" style={{ fontSize: 'clamp(48px, 8vw, 112px)', maxWidth: '12ch' }}>
						{t('exhibitionsHeadingLine1')}<br />
						<span style={{ color: 'var(--muted-gallery)' }}>{t('exhibitionsHeadingLine2')}</span>
					</h1>
					{isAdmin && (
						<div className="mt-8 flex flex-wrap gap-3">
							<Button
								onClick={() => setIsCreateDrawerOpen(true)}
								className="btn-gallery"
							>
								<Icon name="plus" className="mr-2 h-4 w-4" />
								Nieuwe expositie toevoegen
							</Button>
							<Button
								onClick={() => setIsSearchDrawerOpen(true)}
								className="btn-gallery btn-gallery-ghost"
							>
								<Icon name="magnifying-glass" className="mr-2 h-4 w-4" />
								{t('searchExisting')}
							</Button>
						</div>
					)}
				</div>
			</motion.section>

			{/* Newsletter for non-logged-in */}
			{!userId && (
				<div className="container mb-16 max-w-2xl">
					<NewsletterSignup source="exhibitions" />
				</div>
			)}

			{/* Exhibition sections */}
			{sections.map((s) =>
				s.items.length > 0 ? (
					<motion.section
						key={s.key}
						className="py-20"
						style={{
							borderTop: '1px solid var(--line)',
							opacity: s.key === 'past' ? 0.7 : 1,
						}}
						initial="initial"
						whileInView="animate"
						viewport={{ once: true, amount: 0.15 }}
						variants={fadeInUp}
					>
						<div className="container">
							<div className="mb-12 flex items-baseline justify-between">
								<h2 className="display" style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
									{s.label}
								</h2>
								<span className="eyebrow">
									{s.items.length}{' '}
									{s.items.length === 1 ? t('placeSingular') : t('placePlural')}
								</span>
							</div>
							<div className="flex flex-col gap-16">
								{s.items.map((e) => (
									<ExhibitionCard key={e.id} exhibition={e} />
								))}
							</div>
						</div>
					</motion.section>
				) : null,
			)}

			{/* Empty state */}
			{sections.every((s) => s.items.length === 0) && (
				<div className="py-24 text-center">
					<Icon name="clock" className="mx-auto mb-4 h-12 w-12" style={{ color: 'var(--muted-gallery)' }} />
					<h3 className="font-heading mb-2 text-xl font-medium italic">
						{t('exhibitionsEmptyTitle')}
					</h3>
					<p style={{ color: 'var(--muted-gallery)' }}>
						{t('exhibitionsEmptyText')}
					</p>
				</div>
			)}

			{/* Search Exhibition Drawer */}
			{isAdmin && userId && (
				<ExhibitionDrawer
					isOpen={isSearchDrawerOpen}
					onClose={() => setIsSearchDrawerOpen(false)}
					userId={userId}
				/>
			)}

			{/* Create Exhibition Drawer */}
			{isAdmin && isCreateDrawerOpen && (
				<EditExhibitionDrawer
					isOpen={isCreateDrawerOpen}
					onClose={() => setIsCreateDrawerOpen(false)}
					exhibition={null}
				/>
			)}

			{/* Edit Exhibition Drawer */}
			{isAdmin && editingExhibition && (
				<EditExhibitionDrawer
					isOpen={!!editingExhibition}
					onClose={() => setEditingExhibition(null)}
					exhibition={editingExhibition}
				/>
			)}

			{/* Send Details Dialog */}
			{sendDetailsExhibition && (
				<SendEventDetailsDialog
					isOpen={!!sendDetailsExhibition}
					onClose={() => setSendDetailsExhibition(null)}
					exhibition={sendDetailsExhibition}
				/>
			)}

			<style>{`@media (max-width: 760px) {
				article[style*="320px"] { grid-template-columns: 1fr !important; }
			}`}</style>
		</div>
	)
}

export const meta: Route.MetaFunction = () => {
	return buildMeta({
		title: 'Exposities',
		description:
			'Bekijk waar het werk van Else Ringnalda te zien is. Lopende tentoonstellingen, galeries, musea en openbare kunstwerken.',
		url: '/exposities',
	})
}
